import {
  AltiusRes,
  CorrelationMatrixSettings,
  IPortfolioConstructionActualInvestmentData,
  IPortfolioConstructionCompareGraph,
  IPortfolioConstructionCorrelationData,
  IPortfolioConstructionCorrelationItem,
  IPortfolioConstructionDealTreeItem,
  IPortfolioConstructionExpectedReturnData,
  IPortfolioConstructionInvestmentGoal,
  IPortfolioConstructionPieChart,
  IPortfolioConstructionTableRowData,
  IPortfolioConstructionTargetData
} from "store/types";

import { ReportsAction, UserAction } from "../actions";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IPortfolioConstructionActions {
  getInvestmentGoalsAction: (investorId: number) => Promise<IPortfolioConstructionInvestmentGoal>;
  createInvestmentGoalsAction: (
    investorId: number,
    investmentGoals: IPortfolioConstructionInvestmentGoal
  ) => Promise<IPortfolioConstructionInvestmentGoal>;
  getPieChartDataAction: (investorId: number) => Promise<IPortfolioConstructionPieChart[]>;
  getCompareGraphDataAction: (investorId: number) => Promise<IPortfolioConstructionCompareGraph>;
  getCorrelationDataAction: (data: {
    investor_id?: number;
    dateRange: { from: string; to: string };
    items: IPortfolioConstructionCorrelationItem[];
  }) => Promise<IPortfolioConstructionCorrelationData>;

  getDealTreeAction: (investorId?: number) => Promise<IPortfolioConstructionDealTreeItem[]>;
  getPortfolioTableDataAction: (
    investorId: number,
    showEmptyClasses?: boolean
  ) => Promise<IPortfolioConstructionTableRowData[]>;
  addTargetAction: (data: IPortfolioConstructionTargetData) => Promise<void>;
  createAssetClassAction: (name: string) => Promise<IPortfolioConstructionTableRowData>;
  addActualInvestmentAction: (
    data: IPortfolioConstructionActualInvestmentData
  ) => Promise<IPortfolioConstructionTableRowData | void>;
  deleteCustomActualInvestmentAction: (data: IPortfolioConstructionActualInvestmentData) => Promise<void>;
  removeAIconstructionAction: (investorId: number) => Promise<void>;
  deleteCustomAssetClassAction: (id: number) => Promise<number>;
  pinAssetClassAction: (data: {
    asset_class_id: number;
    asset_class_type: "system" | "custom";
    pin: boolean;
  }) => Promise<void>;
  setCorrelationMatrixSettingsAction: (data: CorrelationMatrixSettings) => Promise<void>;
  addExpectedReturnAction: (
    data: IPortfolioConstructionExpectedReturnData
  ) => Promise<IPortfolioConstructionTableRowData | void>;
}

export class PortfolioConstructionActions extends AsyncActions implements IPortfolioConstructionActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public getInvestmentGoalsAction: IPortfolioConstructionActions["getInvestmentGoalsAction"] = async (investorId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPortfolioConstructionInvestmentGoal>>({
      url: `/api/v0.0.3/portfolio-construction/investors/${investorId}/investment-goals`
    });
    return data;
  };

  public createInvestmentGoalsAction: IPortfolioConstructionActions["createInvestmentGoalsAction"] = async (
    investorId,
    investmentGoals
  ) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IPortfolioConstructionInvestmentGoal>>({
      url: `/api/v0.0.3/portfolio-construction/investors/${investorId}/investment-goals`,
      data: investmentGoals
    });
    return data;
  };

  public getPieChartDataAction: IPortfolioConstructionActions["getPieChartDataAction"] = async (investorId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPortfolioConstructionPieChart[]>>({
      url: `/api/v0.0.3/portfolio-construction/investors/${investorId}/pie-chart`
    });
    return data;
  };

  public getCompareGraphDataAction: IPortfolioConstructionActions["getCompareGraphDataAction"] = async (investorId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPortfolioConstructionCompareGraph>>({
      url: `/api/v0.0.3/portfolio-construction/investors/${investorId}/column-chart`
    });
    return data;
  };

  public getCorrelationDataAction: IPortfolioConstructionActions["getCorrelationDataAction"] = async ({ ...body }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IPortfolioConstructionCorrelationData>>({
      url: "/api/v0.0.3/portfolio-construction/investors/correlation-matrix",
      data: body
    });
    return data;
  };

  public getDealTreeAction: IPortfolioConstructionActions["getDealTreeAction"] = async (investorId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPortfolioConstructionDealTreeItem[]>>({
      url: "/api/v0.0.3/portfolio-construction/investors/deal-tree",
      ...(investorId && { params: { investor_id: investorId } })
    });
    return data;
  };

  public getPortfolioTableDataAction: IPortfolioConstructionActions["getPortfolioTableDataAction"] = async (
    investorId,
    showEmptyClasses
  ) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IPortfolioConstructionTableRowData[]>>({
      url: `/api/v0.0.3/portfolio-construction/investors/${investorId}/portfolio`,
      params: {
        show_empty: showEmptyClasses
      }
    });
    this.dispatch({ type: UserAction.SetPortfolioConstruct, payload: investorId });
    return data;
  };

  public addTargetAction: IPortfolioConstructionActions["addTargetAction"] = async (data) => {
    const { investor_id, ...rest } = data;
    await this.api.post<AltiusRes<IPortfolioConstructionTableRowData>>({
      url: `/api/v0.0.3/portfolio-construction/investors/${investor_id}/target`,
      data: rest
    });
  };

  public addActualInvestmentAction: IPortfolioConstructionActions["addActualInvestmentAction"] = async (data) => {
    const { investor_id, ...rest } = data;
    const {
      data: { data: res }
    } = await this.api.post<AltiusRes<IPortfolioConstructionTableRowData>>({
      url: `/api/v0.0.3/portfolio-construction/investors/${investor_id}/actual-investment`,
      data: rest
    });
    if (!Array.isArray(res)) return res;
  };

  public createAssetClassAction: IPortfolioConstructionActions["createAssetClassAction"] = async (name) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IPortfolioConstructionTableRowData>>({
      url: "/api/v0.0.3/portfolio-construction/asset-class",
      data: { name }
    });
    return data;
  };

  public deleteCustomActualInvestmentAction: IPortfolioConstructionActions["deleteCustomActualInvestmentAction"] =
    async (data) => {
      const { investor_id, asset_class_id, asset_class_type } = data;
      await this.api.delete<AltiusRes<IPortfolioConstructionTableRowData>>({
        url: `/api/v0.0.3/portfolio-construction/investors/${investor_id}/actual-investment/asset-class/${asset_class_id}/${asset_class_type}`
      });
    };

  public removeAIconstructionAction: IPortfolioConstructionActions["removeAIconstructionAction"] = async (
    investorId
  ) => {
    await this.api.delete<AltiusRes<IPortfolioConstructionTableRowData>>({
      url: `/api/v0.0.3/portfolio-construction/investors/${investorId}/ai-target`
    });
  };

  public deleteCustomAssetClassAction: IPortfolioConstructionActions["deleteCustomAssetClassAction"] = async (id) => {
    await this.api.delete<AltiusRes<IPortfolioConstructionTableRowData>>({
      url: `/api/v0.0.3/portfolio-construction/asset-class/${id}`
    });
    return id;
  };

  public pinAssetClassAction: IPortfolioConstructionActions["pinAssetClassAction"] = async (data) => {
    await this.api.post<AltiusRes<IPortfolioConstructionTableRowData>>({
      url: "/api/v0.0.3/portfolio-construction/asset-class/pin",
      data
    });
  };

  public setCorrelationMatrixSettingsAction: IPortfolioConstructionActions["setCorrelationMatrixSettingsAction"] =
    async (data) => {
      this.dispatch({
        type: ReportsAction.SetCorrelationMatrixSettings,
        payload: data
      });
    };

  public addExpectedReturnAction: IPortfolioConstructionActions["addExpectedReturnAction"] = async (data) => {
    const { investor_id, asset_class_id, ...rest } = data;
    const {
      data: { data: res }
    } = await this.api.post<AltiusRes<IPortfolioConstructionTableRowData>>({
      url: `/api/v0.0.3/portfolio-construction/investors/${investor_id}/investment-asset-class/${asset_class_id}`,
      data: rest
    });
    if (!Array.isArray(res)) return res;
  };
}
